import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer, LabelList,
} from "recharts";
import { ArrowLeft, Search, MapPin, ExternalLink, TrendingUp, TrendingDown, CheckCircle, Shield } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = ["all", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const STATE_COLORS = {
  NSW: "#6CB2EB", VIC: "#1F2937", QLD: "#7F1D1D",
  WA: "#D97706", SA: "#DC2626", TAS: "#047857", ACT: "#2563EB", NT: "#D946EF",
};

// Rough postcode → state mapping
function postcodeToState(postcode) {
  const n = parseInt(postcode, 10);
  if (!n) return null;
  if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return "ACT";
  if (n >= 1000 && n <= 2599) return "NSW";
  if (n >= 2619 && n <= 2899) return "NSW";
  if (n >= 2921 && n <= 2999) return "NSW";
  if (n >= 3000 && n <= 3999) return "VIC";
  if (n >= 8000 && n <= 8999) return "VIC";
  if (n >= 4000 && n <= 4999) return "QLD";
  if (n >= 9000 && n <= 9999) return "QLD";
  if (n >= 5000 && n <= 5999) return "SA";
  if (n >= 6000 && n <= 6797) return "WA";
  if (n >= 7000 && n <= 7799) return "TAS";
  if (n >= 800 && n <= 899) return "NT";
  if (n >= 900 && n <= 999) return "NT";
  return null;
}

export default function ElectoratePage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [electorates, setElectorates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [postcodeState, setPostcodeState] = useState(null);
  const [aecCandidates, setAecCandidates] = useState(null);
  const [aecLoading, setAecLoading] = useState(false);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "#334155" : "#000000";

  useEffect(() => {
    fetchElectorates();
  }, [stateFilter, searchQuery]);

  const fetchElectorates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stateFilter !== "all") params.state = stateFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const res = await axios.get(`${API}/electorates`, { params });
      setElectorates(res.data);
    } catch (e) {
      setElectorates([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePostcodeSearch = () => {
    if (postcode.length >= 4) {
      const state = postcodeToState(postcode);
      setPostcodeState(state);
      if (state) {
        setStateFilter(state);
        setSearchQuery("");
      }
    }
  };

  const handlePostcodeKey = (e) => {
    if (e.key === "Enter") handlePostcodeSearch();
  };

  const swing = selected?.result_2025?.swing;

  // Fetch AEC official candidates when electorate is selected
  useEffect(() => {
    if (!selected) { setAecCandidates(null); return; }
    const fetchAec = async () => {
      setAecLoading(true);
      setAecCandidates(null);
      try {
        const res = await axios.get(`${API}/aec/division/${selected.id}`);
        setAecCandidates(res.data);
      } catch {
        setAecCandidates(null);
      } finally {
        setAecLoading(false);
      }
    };
    fetchAec();
  }, [selected?.id]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="electorate-page">
      {/* Header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <button
            data-testid="electorate-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Electorate Results
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            How Did Your Electorate Vote?
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            Explore 2025 Federal Election results for 55 key Australian electorates — from Teal seats to safe majors, including NT and ACT.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Postcode Finder */}
        <div
          className="border-2 border-black p-5 mb-6"
          style={{ backgroundColor: surface, boxShadow: "4px 4px 0 #000" }}
          data-testid="postcode-finder"
        >
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4" style={{ color: "#2E5948" }} />
            <h3 className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
              Find Electorates Near You
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              placeholder="Enter your postcode (e.g. 3000)"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={handlePostcodeKey}
              data-testid="postcode-input"
              className="border-2 border-black px-3 py-2 text-sm font-medium focus:outline-none w-48"
              style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text }}
            />
            <button
              onClick={handlePostcodeSearch}
              data-testid="postcode-search-btn"
              className="flex items-center gap-2 px-4 py-2 text-white font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#2E5948", boxShadow: "2px 2px 0 #000" }}
            >
              <Search className="w-4 h-4" /> Find My State
            </button>
            {postcodeState && (
              <div
                className="flex items-center gap-2 px-3 py-2 border-2 border-black text-sm font-bold"
                style={{ backgroundColor: "#FFC82E" }}
              >
                Showing electorates in <span className="font-black">{postcodeState}</span>
                <button
                  onClick={() => { setPostcodeState(null); setStateFilter("all"); setPostcode(""); }}
                  className="ml-1 text-xs underline"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {/* State filter */}
          <div className="flex flex-wrap gap-1" data-testid="state-filter">
            {STATES.map((s) => (
              <button
                key={s}
                data-testid={`state-filter-${s}`}
                onClick={() => { setStateFilter(s); setPostcodeState(null); }}
                className="px-3 py-1.5 border-2 font-bold text-xs transition-all hover:-translate-y-0.5"
                style={{
                  borderColor: "#000",
                  backgroundColor: stateFilter === s ? "#0F172A" : surface,
                  color: stateFilter === s ? "#FFC82E" : text,
                  boxShadow: stateFilter === s ? "2px 2px 0 #000" : "none",
                }}
              >
                {s === "all" ? "All States" : s}
              </button>
            ))}
          </div>

          {/* Name search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textSec }} />
            <input
              type="text"
              placeholder="Search electorate name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="electorate-search-input"
              className="w-full border-2 border-black pl-9 pr-3 py-2 text-sm font-medium focus:outline-none"
              style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Electorate list */}
          <div className="space-y-2">
            <p className="text-xs font-bold mb-3" style={{ color: textSec }}>
              {loading ? "Loading..." : `${electorates.length} electorates shown`}
            </p>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-20 border-2 border-black animate-pulse" style={{ backgroundColor: surface }} />
              ))
            ) : electorates.length === 0 ? (
              <div
                className="py-10 text-center border-2 border-black"
                style={{ backgroundColor: surface }}
              >
                <p className="font-medium" style={{ color: textSec }}>No electorates found. Try a different search.</p>
              </div>
            ) : (
              electorates.map((e) => {
                const isSelected = selected?.id === e.id;
                const winner = e.result_2025;
                const swingDir = winner?.swing >= 0 ? "+" : "";
                return (
                  <button
                    key={e.id}
                    data-testid={`electorate-card-${e.id}`}
                    onClick={() => setSelected(isSelected ? null : e)}
                    className="w-full text-left border-2 border-black p-4 transition-all hover:-translate-y-0.5"
                    style={{
                      backgroundColor: isSelected ? "#0F172A" : surface,
                      boxShadow: isSelected ? "4px 4px 0 #FFC82E" : "2px 2px 0 #000",
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="text-xs font-bold px-1.5 py-0.5 border border-black"
                            style={{
                              backgroundColor: STATE_COLORS[e.state] || "#6B7280",
                              color: "#fff",
                            }}
                          >
                            {e.state}
                          </span>
                          <span className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif", color: isSelected ? "#FFC82E" : text }}>
                            {e.name}
                          </span>
                        </div>
                        {winner && (
                          <p className="text-xs truncate" style={{ color: isSelected ? "rgba(255,255,255,0.7)" : textSec }}>
                            Won by {winner.winner.split(" (")[0]}
                          </p>
                        )}
                      </div>
                      {winner && (
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs font-black" style={{ color: isSelected ? "#fff" : text }}>
                            {winner.margin}% margin
                          </div>
                          <div
                            className="text-xs font-semibold flex items-center gap-0.5 justify-end"
                            style={{ color: winner.swing >= 0 ? "#10B981" : "#C04E3F" }}
                          >
                            {winner.swing >= 0
                              ? <TrendingUp className="w-3 h-3" />
                              : <TrendingDown className="w-3 h-3" />}
                            {swingDir}{winner.swing}%
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail panel */}
          <div>
            {!selected ? (
              <div
                className="border-2 border-black p-8 text-center flex flex-col items-center justify-center min-h-64"
                style={{ backgroundColor: surface, boxShadow: "4px 4px 0 #000" }}
                data-testid="electorate-detail-empty"
              >
                <MapPin className="w-8 h-8 mb-3" style={{ color: textSec }} />
                <p className="font-bold text-sm" style={{ color: textSec }}>
                  Select an electorate to see detailed results
                </p>
              </div>
            ) : (
              <div
                className="border-2 border-black p-5"
                style={{ backgroundColor: surface, boxShadow: "4px 4px 0 #000" }}
                data-testid={`electorate-detail-${selected.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-xs font-bold px-1.5 py-0.5 border border-black"
                        style={{ backgroundColor: STATE_COLORS[selected.state] || "#6B7280", color: "#fff" }}
                      >
                        {selected.state}
                      </span>
                      <span className="text-xs font-semibold" style={{ color: textSec }}>Federal</span>
                    </div>
                    <h2 className="text-2xl font-black" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
                      {selected.name}
                    </h2>
                  </div>
                  <a
                    href={`https://www.aec.gov.au/Elections/federal_elections/2025/${selected.state.toLowerCase()}/divisions/${selected.name.toLowerCase().replace(/\s/g, "")}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="electorate-aec-link"
                    className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 border-2 border-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: "#2E5948", color: "#fff", boxShadow: "2px 2px 0 #000" }}
                  >
                    <ExternalLink className="w-3 h-3" /> AEC
                  </a>
                </div>

                <p className="text-sm mb-4 leading-relaxed" style={{ color: textSec, fontFamily: "Public Sans, sans-serif" }}>
                  {selected.description}
                </p>

                {selected.notable && (
                  <div
                    className="border-l-4 pl-3 py-1 mb-4"
                    style={{ borderColor: "#FFC82E" }}
                  >
                    <p className="text-xs font-semibold italic" style={{ color: textSec }}>
                      {selected.notable}
                    </p>
                  </div>
                )}

                {/* 2025 Result */}
                {selected.result_2025 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-black text-sm uppercase tracking-wider" style={{ color: text }}>
                        2025 Federal Election Result
                      </h3>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="font-bold" style={{ color: text }}>
                          Margin: {selected.result_2025.margin}%
                        </span>
                        <span
                          className="flex items-center gap-0.5 font-bold"
                          style={{ color: selected.result_2025.swing >= 0 ? "#10B981" : "#C04E3F" }}
                        >
                          {selected.result_2025.swing >= 0
                            ? <TrendingUp className="w-3 h-3" />
                            : <TrendingDown className="w-3 h-3" />}
                          Swing {selected.result_2025.swing >= 0 ? "+" : ""}{selected.result_2025.swing}%
                        </span>
                      </div>
                    </div>

                    {/* Winner */}
                    <div
                      className="border-2 border-black p-3 mb-4"
                      style={{ backgroundColor: isDark ? "#0F172A" : "#F0FDF4" }}
                    >
                      <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#2E5948" }}>
                        Winner
                      </p>
                      <p className="font-black text-sm" style={{ color: text }}>
                        {selected.result_2025.winner}
                      </p>
                    </div>

                    {/* Vote chart */}
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={selected.result_2025.results} margin={{ right: 30, left: -10 }}>
                        <XAxis dataKey="party" tick={{ fontSize: 10, fill: text }} />
                        <YAxis domain={[0, 60]} tick={{ fontSize: 10, fill: textSec }} unit="%" />
                        <Tooltip
                          formatter={(v) => [`${v}%`, "Vote Share"]}
                          contentStyle={{ backgroundColor: surface, border: "2px solid #000", borderRadius: 0 }}
                        />
                        <Bar dataKey="votes_pct" radius={0}>
                          <LabelList
                            dataKey="votes_pct"
                            position="top"
                            formatter={(v) => `${v}%`}
                            style={{ fontSize: 10, fill: text }}
                          />
                          {selected.result_2025.results.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Postcode hints */}
                {selected.postcode_hints && (
                  <div className="mt-4">
                    <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: textSec }}>
                      Postcode Areas (approximate)
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.postcode_hints.map((p) => (
                        <span
                          key={p}
                          className="text-xs px-2 py-0.5 border border-black font-medium"
                          style={{ backgroundColor: isDark ? "#0F172A" : "#F3F4F6", color: text }}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AEC Official Candidates */}
                <div className="mt-5" data-testid="aec-candidates-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4" style={{ color: "#2E5948" }} />
                    <h3 className="font-black text-xs uppercase tracking-wider" style={{ color: text }}>
                      2025 Candidates
                    </h3>
                    {aecCandidates && (
                      <span
                        className="ml-auto text-xs px-2 py-0.5 border border-black font-bold flex items-center gap-1"
                        style={{ backgroundColor: "#2E5948", color: "#fff" }}
                        data-testid="aec-verified-badge"
                      >
                        <CheckCircle className="w-3 h-3" /> AEC Official
                      </span>
                    )}
                  </div>

                  {aecLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-10 border border-black animate-pulse" style={{ backgroundColor: isDark ? "#1E293B" : "#F3F4F6" }} />
                      ))}
                    </div>
                  ) : aecCandidates ? (
                    <div className="space-y-1.5" data-testid="aec-candidates-list">
                      {aecCandidates.candidates.map((c, i) => (
                        <div
                          key={c.candidate_id || i}
                          className="flex items-center gap-2 p-2 border border-black text-xs"
                          style={{
                            backgroundColor: c.elected
                              ? (isDark ? "#0F2A1A" : "#F0FDF4")
                              : (isDark ? "#1E293B" : "#FAFAFA"),
                          }}
                          data-testid={`aec-candidate-${i}`}
                        >
                          <div
                            className="w-2 h-8 flex-shrink-0"
                            style={{ backgroundColor: c.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-black truncate" style={{ color: text }}>{c.full_name}</span>
                              {c.elected && (
                                <span className="flex items-center gap-0.5 px-1 text-white font-bold" style={{ backgroundColor: "#10B981", fontSize: "10px" }}>
                                  <CheckCircle className="w-2.5 h-2.5" /> ELECTED
                                </span>
                              )}
                            </div>
                            <span style={{ color: textSec }}>{c.party_name}</span>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-black" style={{ color: text }}>{c.votes_pct}%</div>
                            <div
                              className="flex items-center gap-0.5 justify-end"
                              style={{ color: c.swing >= 0 ? "#10B981" : "#C04E3F", fontSize: "10px" }}
                            >
                              {c.swing >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                              {c.swing >= 0 ? "+" : ""}{c.swing}%
                            </div>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs mt-2" style={{ color: textSec }}>
                        Source: AEC official results · {aecCandidates.total_votes?.toLocaleString()} total votes cast
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs" style={{ color: textSec }}>
                      AEC data not available for this electorate.
                      <a href="https://www.aec.gov.au/elections/federal_elections/2025" target="_blank" rel="noopener noreferrer" className="ml-1 underline font-bold" style={{ color: "#2E5948" }}>
                        View on AEC website
                      </a>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div
          className="mt-8 p-4 border-2 border-black text-xs"
          style={{ backgroundColor: isDark ? "#1E293B" : "#FFF9E6", color: textSec }}
        >
          <strong style={{ color: text }}>Data source:</strong> Candidate results marked "AEC Official" use live data downloaded from the{" "}
          <a href="https://results.aec.gov.au/31496/Website/HouseDownloadsMenu-31496-Csv.htm" target="_blank" rel="noopener noreferrer" className="font-bold underline" style={{ color: "#2E5948" }}>
            AEC 2025 Tally Room
          </a>{" "}
          — official final results as of 12 June 2025. Margin and swing figures shown are first-preference (not 2CP).
        </div>
      </div>
    </div>
  );
}
